import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  collection, query, orderBy, getDocs, limit,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  getDamageClaim, damageClaimStatusBadge,
  reviewDamageClaim, retryDamageClaimCharge,
} from '../../services/disputeService';
import toast from 'react-hot-toast';

export default function AdminClaims() {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedClaim, setSelectedClaim] = useState(null);

  const refresh = () => {
    setLoading(true);
    const q = query(collection(db, 'claims'), orderBy('submittedAt', 'desc'), limit(100));
    getDocs(q)
      .then((snap) => setClaims(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch((err) => toast.error(err.message || 'Could not load claims'))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, []);

  useEffect(() => {
    if (!claimId) { setSelectedClaim(null); return; }
    getDamageClaim(claimId)
      .then(setSelectedClaim)
      .catch((err) => toast.error(err.message || 'Could not load claim'));
  }, [claimId]);

  const filtered = claims.filter(c => {
    if (filter === 'all') return true;
    if (filter === 'pending') return ['submitted', 'admin_reviewing'].includes(c.status);
    if (filter === 'failed') return c.status === 'charge_failed';
    if (filter === 'paid') return c.status === 'approved_paid';
    if (filter === 'rejected') return c.status === 'rejected';
    if (filter === 'withdrawn') return c.status === 'withdrawn';
    return true;
  });

  const pendingCount = claims.filter(c => ['submitted', 'admin_reviewing'].includes(c.status)).length;
  const failedCount = claims.filter(c => c.status === 'charge_failed').length;

  return (
    <div className="p-6">
      <h1 className="font-serif mt-0 mb-2">Damage claims</h1>
      <p className="text-body text-[0.9rem] mb-4">
        Review and approve/reject damage claims submitted by drivers.
      </p>

      <div className="flex gap-1.5 mb-4 flex-wrap">
        {[
          { key: 'pending', label: `Pending${pendingCount > 0 ? ` (${pendingCount})` : ''}` },
          { key: 'failed', label: `Charge failed${failedCount > 0 ? ` (${failedCount})` : ''}` },
          { key: 'paid', label: 'Paid' },
          { key: 'rejected', label: 'Rejected' },
          { key: 'withdrawn', label: 'Withdrawn' },
          { key: 'all', label: 'All' },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full border-[1.5px] cursor-pointer font-semibold text-[0.78rem] ${
              filter === f.key ? 'border-brand-primary text-brand-primary' : 'border-slate-200 bg-white text-body'
            }`}
            style={filter === f.key ? { background: 'rgba(45,212,168,0.06)' } : undefined}>{f.label}</button>
        ))}
      </div>

      <div className="grid gap-4" style={{ gridTemplateColumns: claimId ? '350px 1fr' : '1fr' }}>
        {/* List */}
        <div>
          {loading && <div className="p-5 text-muted">Loading...</div>}
          {!loading && filtered.length === 0 && (
            <div className="p-5 text-muted bg-white rounded-xl border border-slate-200">
              No claims.
            </div>
          )}
          <div className="grid gap-2">
            {filtered.map(c => {
              const badge = damageClaimStatusBadge(c.status);
              const isSelected = c.id === claimId;
              const isUrgent = ['submitted', 'admin_reviewing', 'charge_failed'].includes(c.status);
              return (
                <div key={c.id} onClick={() => navigate(`/admin/claims/${c.id}`)}
                  className="p-3 rounded-[10px] bg-white cursor-pointer text-[0.85rem]"
                  style={{ border: `${isSelected ? '2px' : '1px'} solid ${isSelected ? '#2dd4a8' : isUrgent ? '#fbbf24' : '#e2e8f0'}` }}>
                  <div className="flex justify-between items-start mb-1">
                    <strong className="text-brand-dark">{c.referenceNumber}</strong>
                    <span className="px-2 py-0.5 rounded-full text-[0.68rem] font-bold" style={{ background: badge.bg, color: badge.color }}>
                      {badge.label}
                    </span>
                  </div>
                  <div className="text-body text-[0.78rem]">
                    {c.type} · <strong>${c.amountClaimed?.toFixed(2)}</strong>
                    {c.approvedAmount && c.approvedAmount !== c.amountClaimed && (
                      <> → <strong className="text-emerald-600">${c.approvedAmount.toFixed(2)}</strong></>
                    )}
                  </div>
                  <div className="text-muted text-[0.75rem] mt-0.5">
                    Driver: {c.driverName}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Detail */}
        {claimId && (
          <AdminClaimDetail claim={selectedClaim} onUpdated={() => {
            getDamageClaim(claimId).then(setSelectedClaim);
            refresh();
          }} />
        )}
      </div>
    </div>
  );
}

// ───────── DETAIL ─────────

function AdminClaimDetail({ claim, onUpdated }) {
  const [actionInProgress, setActionInProgress] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [reviewDecision, setReviewDecision] = useState('approve');
  const [approvedAmount, setApprovedAmount] = useState('');
  const [reviewNote, setReviewNote] = useState('');

  useEffect(() => {
    if (claim) setApprovedAmount(claim.amountClaimed?.toString() || '');
  }, [claim?.id]);

  if (!claim) {
    return <div className="p-5 bg-white rounded-xl border border-slate-200">Loading claim...</div>;
  }

  const fmtDate = (ts) => {
    if (!ts) return '';
    const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
    return isNaN(d.getTime()) ? '' : d.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const badge = damageClaimStatusBadge(claim.status);
  const canReview = ['submitted', 'admin_reviewing'].includes(claim.status);
  const canRetry = claim.status === 'charge_failed';

  const handleReviewSubmit = async () => {
    if (reviewDecision === 'approve') {
      const amt = parseFloat(approvedAmount);
      if (isNaN(amt) || amt <= 0) { toast.error('Enter a valid approved amount'); return; }
      if (amt > claim.amountClaimed) { toast.error('Cannot exceed claimed amount'); return; }
    }
    if (!reviewNote || reviewNote.trim().length < 5) {
      toast.error('Notes are required (at least 5 chars)');
      return;
    }
    setActionInProgress(true);
    try {
      const result = await reviewDamageClaim({
        claimId: claim.id,
        decision: reviewDecision,
        approvedAmount: reviewDecision === 'approve' ? parseFloat(approvedAmount) : null,
        note: reviewNote,
      });
      if (reviewDecision === 'approve') {
        if (result.chargeOk && result.payoutOk) toast.success(`Approved $${approvedAmount} — rider charged, driver paid`);
        else if (result.chargeOk) toast.success(`Approved $${approvedAmount} — rider charged, driver payout pending`);
        else toast.error(`Charge failed: ${result.error}`);
      } else {
        toast.success('Claim rejected');
      }
      setShowReview(false);
      setReviewNote('');
      onUpdated && onUpdated();
    } catch (err) {
      toast.error(err.message || 'Review failed');
    }
    setActionInProgress(false);
  };

  const handleRetry = async () => {
    if (!window.confirm(`Retry charging rider $${claim.approvedAmount?.toFixed(2)}? This will attempt to charge their saved payment method again.`)) return;
    setActionInProgress(true);
    try {
      const result = await retryDamageClaimCharge({ claimId: claim.id });
      if (result.success) {
        toast.success('Charge succeeded');
      } else {
        toast.error(`Retry failed: ${result.error}`);
      }
      onUpdated && onUpdated();
    } catch (err) {
      toast.error(err.message || 'Retry failed');
    }
    setActionInProgress(false);
  };

  return (
    <div className="p-5 bg-white rounded-xl border border-slate-200 max-h-[85vh] overflow-y-auto">
      {/* Header */}
      <div className="flex justify-between items-start mb-3">
        <div>
          <h2 className="m-0 text-[1.2rem]">{claim.referenceNumber}</h2>
          <div className="text-[0.78rem] text-muted mt-0.5 capitalize">
            {claim.type} · Submitted {fmtDate(claim.submittedAt)}
          </div>
        </div>
        <span className="px-3 py-1 rounded-full text-[0.78rem] font-bold" style={{ background: badge.bg, color: badge.color }}>
          {badge.label}
        </span>
      </div>

      {/* Amount panel */}
      <div className="p-3.5 rounded-[10px] bg-slate-50 mb-3 flex justify-between items-center">
        <div>
          <div className="text-[0.78rem] text-muted font-semibold">Claimed</div>
          <div className="text-[1.4rem] font-bold text-brand-dark">${claim.amountClaimed?.toFixed(2)}</div>
        </div>
        {claim.approvedAmount != null && (
          <div className="text-right">
            <div className="text-[0.78rem] text-muted font-semibold">Approved</div>
            <div className="text-[1.4rem] font-bold text-emerald-600">${claim.approvedAmount.toFixed(2)}</div>
          </div>
        )}
      </div>

      {/* Parties */}
      <div className="p-3 rounded-[10px] bg-slate-50 mb-3 text-[0.82rem]">
        <div><strong>Driver:</strong> {claim.driverName} ({claim.driverId?.substring(0, 8)})</div>
        <div><strong>Rider:</strong> {claim.riderName || '—'} ({claim.riderId?.substring(0, 8)})</div>
        <div><strong>Trip:</strong> {claim.bookingReferenceNumber || claim.bookingId?.substring(0, 8)}</div>
      </div>

      {/* Description */}
      <div className="mb-3">
        <div className="text-[0.78rem] text-muted font-semibold uppercase mb-1">
          Description
        </div>
        <div className="text-[0.85rem] text-brand-dark whitespace-pre-wrap p-2.5 rounded-lg bg-slate-50">
          {claim.description}
        </div>
      </div>

      {/* Photos */}
      {claim.photos?.length > 0 && (
        <div className="mb-3">
          <div className="text-[0.78rem] text-muted font-semibold mb-1.5">
            Damage photos ({claim.photos.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {claim.photos.map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                <img src={url} alt={`Damage ${i + 1}`}
                  className="w-20 h-20 object-cover rounded-md border border-slate-200" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Receipts */}
      {claim.receiptPhotos?.length > 0 && (
        <div className="mb-3">
          <div className="text-[0.78rem] text-muted font-semibold mb-1.5">
            Receipts ({claim.receiptPhotos.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {claim.receiptPhotos.map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                className="px-2.5 py-1.5 rounded-md bg-slate-50 border border-slate-200 text-[0.78rem] text-brand-dark no-underline">📄 Receipt {i + 1}</a>
            ))}
          </div>
        </div>
      )}

      {/* Charge / payout status */}
      {(claim.riderChargedAt || claim.driverPayoutAt || claim.lastChargeError) && (
        <div className="p-3 rounded-[10px] bg-slate-50 mb-3 text-[0.82rem]">
          {claim.riderChargedAt && (
            <div>✓ <strong>Rider charged:</strong> {fmtDate(claim.riderChargedAt)} (PI: {claim.riderPaymentIntentId?.substring(0, 14)}...)</div>
          )}
          {claim.driverPayoutAt && (
            <div>✓ <strong>Driver paid:</strong> {fmtDate(claim.driverPayoutAt)} (ID: {claim.driverPayoutId?.substring(0, 14)}...)</div>
          )}
          {claim.lastChargeError && (
            <div className="text-red-600 mt-1">⚠ <strong>Last error:</strong> {claim.lastChargeError}</div>
          )}
          {claim.chargeAttempts > 0 && (
            <div className="text-muted mt-1">Charge attempts: {claim.chargeAttempts}</div>
          )}
        </div>
      )}

      {claim.adminNotes && (
        <div className="p-3 rounded-[10px] mb-3 text-[0.85rem]" style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e' }}>
          <div className="font-semibold mb-1">Admin notes:</div>
          <div className="italic">"{claim.adminNotes}"</div>
        </div>
      )}

      {claim.withdrawnReason && (
        <div className="p-3 rounded-[10px] bg-slate-50 border border-slate-200 mb-3 text-[0.85rem] text-body">
          Withdrawn by driver: "{claim.withdrawnReason}"
        </div>
      )}

      {/* Review action */}
      {canReview && !showReview && (
        <button onClick={() => setShowReview(true)}
          className="w-full p-3 rounded-[10px] border-0 bg-brand-primary text-white cursor-pointer font-bold">Review this claim</button>
      )}

      {canReview && showReview && (
        <div className="p-3.5 rounded-[10px] bg-white border-[1.5px] border-brand-primary">
          <h3 className="mt-0 mb-2.5 text-[0.95rem]">Review decision</h3>

          <div className="form-group mb-2">
            <div className="grid grid-cols-2 gap-1.5">
              {[
                { value: 'approve', label: '✓ Approve', color: '#2dd4a8' },
                { value: 'reject', label: '✗ Reject', color: '#dc2626' },
              ].map(opt => (
                <label key={opt.value} className="block p-2.5 rounded-lg cursor-pointer text-center font-semibold text-[0.85rem]"
                  style={{
                    border: `1.5px solid ${reviewDecision === opt.value ? opt.color : '#e2e8f0'}`,
                    background: reviewDecision === opt.value ? `${opt.color}10` : '#fff',
                    color: reviewDecision === opt.value ? opt.color : '#5a6578',
                  }}>
                  <input type="radio" name="decision" value={opt.value}
                    checked={reviewDecision === opt.value}
                    onChange={(e) => setReviewDecision(e.target.value)}
                    className="hidden" />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {reviewDecision === 'approve' && (
            <div className="form-group mb-2">
              <label className="form-label text-[0.82rem]">Approved amount * (max ${claim.amountClaimed?.toFixed(2)})</label>
              <input type="number" className="form-input" step="0.01" min="0" max={claim.amountClaimed}
                value={approvedAmount}
                onChange={(e) => setApprovedAmount(e.target.value)} />
            </div>
          )}

          <div className="form-group mb-2.5">
            <label className="form-label text-[0.82rem]">
              Notes * {reviewDecision === 'reject' && '(reason for rejection)'}
            </label>
            <textarea className="form-input" rows={2}
              placeholder={reviewDecision === 'approve' ? 'Notes about the decision' : 'Explain why this is being rejected'}
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)} />
          </div>

          <div className="flex gap-1.5">
            <button onClick={() => setShowReview(false)}
              className="flex-1 p-2 rounded-lg border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold text-[0.82rem]">Cancel</button>
            <button onClick={handleReviewSubmit} disabled={actionInProgress}
              className="flex-1 p-2 rounded-lg border-0 text-white cursor-pointer font-bold text-[0.82rem]"
              style={{ background: reviewDecision === 'approve' ? '#2dd4a8' : '#dc2626' }}>{actionInProgress ? '...' : `Confirm ${reviewDecision}`}</button>
          </div>
        </div>
      )}

      {/* Retry action */}
      {canRetry && (
        <button onClick={handleRetry} disabled={actionInProgress}
          className="w-full p-3 rounded-[10px] border-0 bg-red-600 text-white cursor-pointer font-bold">{actionInProgress ? 'Retrying...' : `🔄 Retry charge $${claim.approvedAmount?.toFixed(2)}`}</button>
      )}

      {/* Status history */}
      {claim.statusHistory && claim.statusHistory.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-[0.82rem] text-body font-semibold">
            Claim history ({claim.statusHistory.length})
          </summary>
          <div className="mt-1.5 p-2.5 rounded-lg bg-slate-50 text-[0.78rem] text-body">
            {claim.statusHistory.map((h, i) => (
              <div key={i} className="mb-1.5 pb-1.5" style={{ borderBottom: i < claim.statusHistory.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                <div><strong>{h.from || '—'}</strong> → <strong>{h.to}</strong></div>
                {h.note && <div className="italic">"{h.note}"</div>}
                <div className="text-muted">{fmtDate(h.at)} by {h.by === 'system' ? 'system' : h.by?.substring(0, 8)}</div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

const TERMINAL = ['resolved_returned', 'resolved_collected', 'unresolved',
  'expired_driver_custody', 'unresolved_pickup_timeout', 'cancelled_by_rider', 'admin_resolved'];